import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { HiSearchCircle, HiCheckCircle, HiOutlineLightningBolt, HiOutlineShieldCheck, HiOutlineArrowNarrowLeft, HiOutlineShoppingBag } from 'react-icons/hi';
import { MdOutlineArchitecture, MdMeetingRoom, MdOutlineLayers } from 'react-icons/md';
import { useCart } from '../../context/CartContext';
import ScrollToTop from '../ui/ScrollToTop';
import { Helmet } from 'react-helmet-async';
import { IoMdStar } from "react-icons/io"; // Estrellas clásicas
import { motion } from "framer-motion";
import { HiArrowNarrowRight } from "react-icons/hi"


const PlanLevel = ({ level }) => {
    return (
        <div className="flex flex-col gap-1.5 pt-4">
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-400">
                Nivel de Complejidad
            </span>

            <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((step) => (
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: step * 0.1 }}
                    >
                        <IoMdStar
                            size={18}
                            className={`${step <= level
                                ? "text-yellow-400" // Color activo
                                : "text-gray-200"    // Color apagado
                                }`}
                        />
                    </motion.div>
                ))}

                {/* Un pequeño indicador de texto al lado */}
                <span className="ml-2 text-[10px] font-bold text-gray-500 self-center">
                    {level}/5
                </span>
            </div>
        </div>
    );
};

// Sub-componente para Specs con opción de resaltado
const SpecItem = ({ label, value, icon: Icon, featured = false }) => (
    <div className={`flex items-center gap-4 p-4 rounded-2xl border transition-all hover:shadow-md group ${featured
        ? "bg-[#F26B1D]/5 border-[#F26B1D]/20 shadow-sm"
        : "bg-gray-50 border-gray-100 hover:bg-white"
        }`}>
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl shadow-sm transition-colors ${featured
            ? "bg-[#F26B1D] text-white"
            : "bg-white group-hover:bg-[#F26B1D]/10 group-hover:text-[#F26B1D]"
            }`}>
            {Icon}
        </div>
        <div>
            <p className={`text-[10px] font-black uppercase tracking-widest leading-none mb-1 ${featured ? "text-[#F26B1D]" : "text-gray-400"
                }`}>{label}</p>
            <p className="text-sm font-bold text-[#1C1C1C]">{value}</p>
        </div>
    </div>
);

const PaymentMethods = ({ className }) => (
    <div className={`mt-10 pt-[120px] border-t border-gray-100 ${className}`}>

        <div className="mt-6 text-center py-8">
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
            >
                <Link
                    to="/metodos-de-pago"
                    className="inline-flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-[#F26B1D] group"
                >
                    <span className="bg-[#F26B1D]/5 px-4 py-2 rounded-full group-hover:bg-[#F26B1D] group-hover:text-white transition-all duration-300 border border-[#F26B1D]/20">
                        ¿Cómo comprar y recibir mi plano?
                    </span>

                    <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="text-[#F26B1D]"
                    >
                        <HiArrowNarrowRight size={18} />
                    </motion.div>
                </Link>
            </motion.div>
        </div>

        <p className="text-[10px] md:text-[11px] font-black text-gray-400 uppercase tracking-[0.3em] mb-6 md:mb-8 text-center">
            Pagos Seguros y Encriptados
        </p>
        {/* Contenedor de Logos: 
            - Mobile: Color natural, tamaño reducido (h-6/h-7), gap pequeño.
            - Desktop: Grayscale por defecto, tamaño grande (h-8/h-10), gap amplio.
        */}
        <div className="flex flex-wrap items-center justify-center gap-5 md:gap-8 transition-all duration-500">
            {/* Yape */}
            <img
                src="https://images.seeklogo.com/logo-png/39/1/yape-app-logo-png_seeklogo-399697.png"
                alt="Yape"
                className="h-7 md:h-10 w-auto md:grayscale md:hover:grayscale-0 transition-all object-contain"
            />

            {/* Plin */}
            <img
                src="https://images.seeklogo.com/logo-png/38/1/plin-logo-png_seeklogo-386806.png"
                alt="Plin"
                className="h-6 md:h-8 w-auto md:grayscale md:hover:grayscale-0 transition-all object-contain"
            />

            {/* Visa */}
            <img
                src="https://upload.wikimedia.org/wikipedia/commons/d/d3/Visa_Inc._logo_%282005%E2%80%932014%29.png"
                alt="Visa"
                className="h-4 md:h-6 w-auto md:grayscale md:hover:grayscale-0 transition-all object-contain"
            />

            {/* Mastercard */}
            <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png"
                alt="Mastercard"
                className="h-7 md:h-9 w-auto md:grayscale md:hover:grayscale-0 transition-all object-contain"
            />

            {/* PayPal */}
            <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRlwIwNSX1Gmr5b7N-G1FCAH-xI881rZR6ZQw&s"
                alt="PayPal"
                className="h-6 md:h-8 w-auto md:grayscale md:hover:grayscale-0 transition-all object-contain"
            />
        </div>
        {/* Badge de Garantía - Más compacto en mobile */}
        <div className="mt-6 md:mt-8 flex items-center justify-center gap-2 md:gap-3 py-3 md:py-4 px-4 md:px-6 bg-gray-50 rounded-2xl border border-gray-100">
            <HiOutlineShieldCheck className="text-[#F26B1D] text-lg" />
            <span className="text-[9px] md:text-[11px] font-black uppercase tracking-tighter text-gray-500 text-center">
                Garantía Planix: Envios seguros a tu correo electrónico, con soporte dedicado para cualquier consulta o inconveniente.
            </span>
        </div>
    </div>
);

export default function PlanDetail({ plans }) {
    const { id } = useParams();
    const plan = plans.find(p => p.id === Number(id));
    const [selectedImg, setSelectedImg] = useState(0);
    const { addToCart, cart } = useCart();

    if (!plan) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                Plano no encontrado
            </div>
        );
    }

    const isInCart = cart.some(item => item.id === plan.id);

    const priceUSD = Math.round(plan.price / 3.8);

    if (!plan) return <div className="min-h-screen flex items-center justify-center font-black uppercase tracking-tighter text-gray-300 text-3xl">Plano no encontrado</div>;

    return (

        <>
            <ScrollToTop />

            <Helmet>
                <title>
                    {`${plan.name} | Planos Arquitectónicos Profesionales - Planix`}
                </title>

                <meta
                    name="description"
                    content={`${plan.name}. Plano arquitectónico de ${plan.area} con ${plan.rooms} dormitorios y ${plan.floors} niveles. Descarga digital en PDF y AutoCAD (DWG). Diseños modernos adaptables a distintos países.`}
                />

                <meta
                    name="keywords"
                    content={`
      planos de casas,
      planos arquitectónicos,
      house plans,
      planos PDF,
      planos DWG,
      diseños de casas modernas,
      planos para construir,
      architectural plans,
      modern house plans,
      ${plan.category},
      ${plan.style}
    `}
                />

                <meta name="robots" content="index, follow" />

                {/* Canonical */}
                <link
                    rel="canonical"
                    href={`https://www.planixapp.com/planos/${plan.id}`}
                />

                {/* Open Graph */}
                <meta property="og:type" content="product" />
                <meta property="og:title" content={plan.name} />
                <meta
                    property="og:description"
                    content={`Plano profesional de ${plan.area} - Descarga inmediata en PDF y AutoCAD.`}
                />
                <meta property="og:image" content={plan.images[0]} />
                <meta
                    property="og:url"
                    content={`https://www.planixapp.com/planos/${plan.id}`}
                />

                {/* Twitter */}
                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />
                <meta name="twitter:title" content={plan.name} />
                <meta
                    name="twitter:description"
                    content={`Plano profesional descargable en PDF y DWG.`}
                />
                <meta
                    name="twitter:image"
                    content={plan.images[0]}
                />

                {/* Product Schema */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org/",
                        "@type": "Product",
                        "name": plan.name,
                        "image": plan.images,
                        "description": plan.description,
                        "brand": {
                            "@type": "Brand",
                            "name": "Planix"
                        },
                        "offers": {
                            "@type": "Offer",
                            "url": `https://www.planixapp.com/planos/${plan.id}`,
                            "priceCurrency": "PEN",
                            "price": plan.price,
                            "availability": "https://schema.org/InStock"
                        }
                    })}
                </script>
            </Helmet>

            <div className="min-h-screen bg-white pb-20">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
                    <div className="grid lg:grid-cols-12 gap-16 pt-8">
                        {/* SECCIÓN IZQUIERDA: GALERÍA VISUAL */}
                        <div className="lg:col-span-7 w-full max-w-full overflow-hidden">

                            <div className="space-y-4 sm:space-y-6 lg:sticky lg:top-24">
                                <div className="
  relative group 
  bg-[#F9F9F9] 
  rounded-2xl sm:rounded-[2.5rem] 
  overflow-hidden 
  border border-gray-100 
  shadow-xl sm:shadow-2xl
">
                                    {/* Badge de Estilo Flotante */}
                                    <div className="
  absolute top-4 left-4 sm:top-6 sm:left-6 z-10 
  bg-white/90 backdrop-blur-md 
  px-3 py-1.5 sm:px-4 sm:py-2 
  rounded-xl sm:rounded-2xl 
  text-[8px] sm:text-[10px] 
  font-black flex items-center gap-2 
  shadow-sm uppercase tracking-[0.08em] sm:tracking-[0.1em]
">
                                        <div className="w-2 h-2 rounded-full bg-[#F26B1D] animate-pulse" />
                                        Estilo {plan.style}
                                    </div>

                                    <Zoom overlayBgColorEnd="rgba(28, 28, 28, 0.98)">
                                        <img
                                            src={plan.images[selectedImg]}
                                            className="
    w-full 
    aspect-[1/1] sm:aspect-[4/3] 
    object-contain 
    p-3 sm:p-6 md:p-8 
    transition-transform duration-700 
    group-hover:scale-105
  "
                                            alt={plan.name}
                                        />
                                    </Zoom>
                                </div>

                                {/* Thumbnails con scroll invisible */}
                                <div className="
  flex gap-3 sm:gap-4 
  overflow-x-auto pb-2 
  no-scrollbar 
  max-w-full
">
                                    {plan.images.map((img, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setSelectedImg(i)}
                                            className={`
    relative 
    min-w-[80px] sm:min-w-[100px] md:min-w-[110px]
    h-20 sm:h-24 
    rounded-xl sm:rounded-2xl 
    overflow-hidden border-2 transition-all flex-shrink-0
    ${selectedImg === i
                                                    ? "border-[#F26B1D] scale-95 shadow-lg"
                                                    : "border-transparent opacity-60 hover:opacity-100"
                                                }
  `}
                                        >
                                            <img src={img} className="w-full h-full object-cover" alt={`Vista ${i}`} />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Sección de Métodos de Pago */}
                            <PaymentMethods className="hidden lg:block border-white/5" />
                        </div>
                        {/* SECCIÓN DERECHA: INFO Y CONFIGURACIÓN */}
                        <div className="lg:col-span-5 space-y-10">

                            <button
                                onClick={() => window.history.back()}
                                className="group flex items-center gap-2 text-gray-400 hover:text-[#1C1C1C] transition-colors"
                            >
                                <HiOutlineArrowNarrowLeft />
                                <span className="text-[10px] font-black uppercase tracking-[0.3em]">Regresar</span>
                            </button>

                            {/* Cabecera */}
                            <header>
                                <div className="flex items-center gap-3 mb-6">
                                    <span className="bg-[#1C1C1C] text-white px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.2em]">
                                        {plan.category}
                                    </span>
                                    <span className="bg-[#F26B1D] text-white px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.2em]">
                                        {plan.badge}
                                    </span>
                                    <span className="text-gray-300 text-[10px] font-bold uppercase tracking-widest">
                                        ID: #{plan.id}
                                    </span>
                                </div>

                                <h1 className="
  text-3xl sm:text-3xl md:text-4xl lg:text-5xl 
  font-black text-[#1C1C1C] 
  leading-[1] mb-6 uppercase tracking-tight
">
                                    {plan.name.split(' ').map((word, i) => (
                                        <span key={i} className={i === 0 ? "block" : "text-[#F26B1D]"}>{word} </span>
                                    ))}
                                </h1>

                                <p className="text-sm sm:text-base md:text-lg text-gray-500 leading-relaxed font-medium">
                                    {plan.description}
                                </p>
                            </header>

                            {/* Grid de Especificaciones con Iconos Pro */}
                            <div className="pt-6">
                                <PlanLevel level={plan.level} />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <SpecItem featured label="Cantidad de Láminas" value={plan.cantidLaminas} icon={<MdOutlineArchitecture size={20} />} />
                                <SpecItem label="Frente x Fondo" value={`${plan.front} x ${plan.depth} m`} icon={<HiSearchCircle size={20} />} />
                                <SpecItem label="Área Construida" value={plan.area} icon={<MdOutlineArchitecture size={20} />} />
                                <SpecItem label="Dormitorios" value={plan.rooms} icon={<MdMeetingRoom size={20} />} />
                                <SpecItem label="Niveles" value={`${plan.floors} Piso(s)`} icon={<MdOutlineLayers size={20} />} />
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-6 border-t border-gray-100">

                                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                                    <p className="text-[10px] uppercase font-black tracking-widest text-gray-400">
                                        Tipo
                                    </p>
                                    <h4 className="text-sm font-black text-[#1C1C1C] mt-2">
                                        {plan.planType}
                                    </h4>
                                </div>

                                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                                    <p className="text-[10px] uppercase font-black tracking-widest text-gray-400">
                                        Paquete
                                    </p>
                                    <h4 className="text-sm font-black text-[#1C1C1C] mt-2">
                                        {plan.packageType}
                                    </h4>
                                </div>

                                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                                    <p className="text-[10px] uppercase font-black tracking-widest text-gray-400">
                                        Formato
                                    </p>
                                    <h4 className="text-sm font-black text-[#1C1C1C] mt-2">
                                        {plan.formats.join(" / ")}
                                    </h4>
                                </div>

                                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                                    <p className="text-[10px] uppercase font-black tracking-widest text-gray-400">
                                        Estilo
                                    </p>
                                    <h4 className="text-sm font-black text-[#1C1C1C] mt-2">
                                        {plan.style}
                                    </h4>
                                </div>

                            </div>

                            {/* Detalles Técnicos - Lo que incluye */}
                            <div className="space-y-6 pt-6">
                                <h3 className="text-xs font-black text-[#1C1C1C] uppercase tracking-[0.3em] flex items-center gap-3">
                                    <HiOutlineShieldCheck className="text-[#F26B1D] text-2xl" />
                                    Documentación Técnica Incluida
                                </h3>

                                <div className="space-y-4 pt-8 border-t border-gray-100">

                                    <h3 className="text-xs font-black uppercase tracking-[0.3em] text-[#1C1C1C]">
                                        Especialidades Incluidas
                                    </h3>

                                    <div className="flex flex-wrap gap-3">

                                        {plan.electric && (
                                            <div className="bg-yellow-50 border border-yellow-100 px-4 py-3 rounded-2xl flex items-center gap-2">
                                                ⚡
                                                <span className="text-xs font-bold text-yellow-800">
                                                    Instalaciones Eléctricas
                                                </span>
                                            </div>
                                        )}

                                        {plan.sanitary && (
                                            <div className="bg-cyan-50 border border-cyan-100 px-4 py-3 rounded-2xl flex items-center gap-2">
                                                💧
                                                <span className="text-xs font-bold text-cyan-800">
                                                    Instalaciones Sanitarias
                                                </span>
                                            </div>
                                        )}

                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8">
                                    {plan.includes.map((item, i) => (
                                        <div key={i} className="flex items-center gap-3 group">
                                            <div className="w-1.5 h-1.5 rounded-full bg-gray-200 group-hover:bg-[#F26B1D] transition-colors" />
                                            <span className="text-xs font-bold text-gray-600 uppercase tracking-tight group-hover:text-[#1C1C1C] transition-colors">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Aviso Importante con diseño destacado */}
                            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 mb-4">
                                <div className="flex gap-3">
                                    <span className="text-lg">⚠️</span>
                                    <div className="space-y-2">
                                        <h4 className="text-[10px] font-black text-amber-800 uppercase tracking-widest">
                                            Aviso Importante
                                        </h4>
                                        <p className="text-[10px] leading-snug text-amber-700/90">
                                            <b>Diseño Solamente referencial. </b> Requiere validación de un <b>profesional colegiado</b> Y adaptaciones según normativas locales y características del terreno.
                                        </p>

                                        {/* Botones de Acción Rápida */}
                                        <div className="flex gap-2 pt-1">
                                            <Link
                                                to="/preguntas-frecuentes"
                                                className="bg-amber-100 hover:bg-amber-200 text-amber-900 text-[9px] font-black uppercase tracking-tighter px-3 py-1.5 rounded-lg transition-colors border border-amber-200/50 flex items-center gap-1"
                                            >
                                                <span>❓</span> Preguntas
                                            </Link>
                                            <Link
                                                to="/terminos-y-condiciones"
                                                className="bg-amber-100 hover:bg-amber-200 text-amber-900 text-[9px] font-black uppercase tracking-tighter px-3 py-1.5 rounded-lg transition-colors border border-amber-200/50 flex items-center gap-1"
                                            >
                                                <span>📜</span> Términos
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                            </div>

                            {/* Caja de Acción y Precio */}
                            <div className="
  bg-[#1C1C1C] 
  rounded-3xl 
  p-5 sm:p-6 md:p-10 
  text-white shadow-2xl 
  relative overflow-hidden
">
                                <div className="relative z-10">
                                    <p className="text-[#F26B1D] text-[10px] font-black uppercase tracking-[0.4em] mb-4">Inversión del Proyecto</p>
                                    <div className="flex flex-wrap items-baseline gap-2 sm:gap-4 mb-6 sm:mb-8">
                                        {Number(plan.originalPrice) > plan.price && (
                                            <span className="text-xl text-white/30 line-through font-bold italic">
                                                S/ {plan.originalPrice}
                                            </span>
                                        )}
                                        <span className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tighter">
                                            S/ {plan.price}
                                        </span>

                                        <span className="text-sm sm:text-base font-medium text-black-400 bg-indigo-500 hover:bg-fuchsia-500 px-2 py-1 rounded-lg">
                                            ≈ $ {priceUSD}
                                        </span>
                                    </div>

                                    <button
                                        onClick={() => { if (!isInCart) addToCart(plan) }}
                                        className={`
    w-full py-6 rounded-2xl font-black uppercase tracking-[0.2em] text-sm
    transition-all duration-500 transform active:scale-95 shadow-xl
    flex items-center justify-center gap-3

    ${isInCart
                                                ? "bg-green-500 text-white hover:bg-green-600"
                                                : "bg-[#F26B1D] text-white hover:bg-white hover:text-[#1C1C1C]"
                                            }
  `}
                                    >
                                        {isInCart ? (
                                            <>
                                                <HiCheckCircle size={22} className="shrink-0" />
                                                <span>En tu carrito</span>
                                            </>
                                        ) : (
                                            <>
                                                <HiOutlineShoppingBag size={22} className="shrink-0" />
                                                <span>Comprar</span>
                                            </>
                                        )}
                                    </button>

                                    {/* Mini Lista de Bonos */}
                                    {plan.bonus && plan.bonus.length > 0 && (
                                        <div className="mt-8 flex items-center justify-center gap-2 text-[#F26B1D] animate-bounce">
                                            <HiOutlineLightningBolt />
                                            <span className="text-[10px] font-black uppercase tracking-widest">Incluye: {plan.bonus[0]}</span>
                                        </div>
                                    )}

                                    <p className="mt-6 text-center text-[10px] text-gray-400 font-medium italic">
                                        * Entrega inmediata en formato digital (PDF / CAD)
                                    </p>
                                </div>
                                {/* Decoración visual interna */}
                                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#F26B1D]/10 rounded-full blur-3xl" />
                            </div>

                            <div className="pt-6 space-y-4">
                                <a href="https://wa.me/51983974833?text=Deseo%20un%20plano%20personalizado%20para%20mi%20proyecto" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#25D366] hover:text-[#1C1C1C] transition-colors bg-[#25D366]/10 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-[0.3em]">
                                    <HiOutlineShieldCheck className="text-[#25D366] text-lg" />
                                    <span className="text-[10px] font-black uppercase tracking-[0.3em]">
                                        <HiOutlineArrowNarrowLeft className="rotate-180" size={24} /> ¿Necesitas un plano personalizado?
                                    </span>
                                </a>
                                <span className="text-[12px] text-gray-500 font-medium italic">
                                    Si no encuentras el plano ideal para tu proyecto, contáctanos para una cotización personalizada. Ofrecemos planos a medida, adaptados a tus necesidades y terreno, con la misma calidad y garantía Planix.
                                </span>
                            </div>

                            {/* Ideal Para Tags */}
                            <div className="flex flex-wrap gap-2 pt-6">
                                {/* Verificamos que exista y que sea un array antes de mapear */}
                                {Array.isArray(plan.idealFor) && plan.idealFor.map((tag, i) => (
                                    <span key={i} className="px-4 py-1.5 bg-gray-50 text-gray-400 text-[9px] font-black rounded-lg uppercase border border-gray-100 tracking-widest">
                                        {/* Aquí 'tag' es el string "profesionales", por lo que .replace sí funciona */}
                                        #{tag.replace(/\s+/g, '_')}
                                    </span>
                                ))}
                            </div>

                            <div className="pt-8 border-t border-gray-100 space-y-4">

                                <h3 className="text-xs font-black uppercase tracking-[0.3em] text-[#1C1C1C]">
                                    Características Destacadas
                                </h3>

                                <div className="grid grid-cols-1 gap-3">

                                    <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-start gap-3">
                                        <span className="text-xl">🏢</span>
                                        <div>
                                            <h4 className="text-sm font-black text-[#1C1C1C]">
                                                Proyecto Multifamiliar
                                            </h4>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Distribución optimizada para vivienda colectiva.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-start gap-3">
                                        <span className="text-xl">🌤️</span>
                                        <div>
                                            <h4 className="text-sm font-black text-[#1C1C1C]">
                                                Balcones y Ventilación
                                            </h4>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Mayor iluminación y confort ambiental.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-start gap-3">
                                        <span className="text-xl">🧺</span>
                                        <div>
                                            <h4 className="text-sm font-black text-[#1C1C1C]">
                                                Azotea Funcional
                                            </h4>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Lavandería y tendal incluidos.
                                            </p>
                                        </div>
                                    </div>

                                </div>

                            </div>

                            <div className="pt-8 border-t border-gray-100">

                                <h3 className="text-xs font-black uppercase tracking-[0.3em] text-[#1C1C1C] mb-4">
                                    Ideal Para
                                </h3>

                                <div className="bg-[#F9F9F9] border border-gray-100 rounded-3xl p-6">
                                    <p className="text-sm leading-relaxed font-semibold text-gray-600">
                                        {plan.idealFor}
                                    </p>
                                </div>

                            </div>

                            <PaymentMethods className="lg:hidden" />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}